import { isAfter, isEqual, isBefore, parseISO } from "date-fns/fp"
import Calendar from "react-calendar"
import { CalendarContainer, CapsuleTabContentBody } from "../CapsuleComponents"
import { createRef, useEffect, useRef, useState } from "react"
import { QuickPriceCalculatorLister, QuickPriceCalculatorTypeLister } from "../../../semshared/pricelist/quickprice_lister";
import { AvailabilityType } from "../../../semshared/availability/availability";
import Divider from "../../Divider";
import { LinkButton } from "../../LinkButton";
import i18n from "../../../i18n";

interface CalendarSectionProps {
  onCalChanged: any
  calValue: any
  fullScreen: boolean
  drawer: boolean
  vertical: boolean
  quickPriceCalculator: QuickPriceCalculatorTypeLister | null
  availabilityChecker: AvailabilityType | null
  setDrawerShow: (s: string) => void;
  setPopupShow: (s: string | null) => void;
}

/**
 * Calendar step for selecting a booking date range.
 * @param props - Range value, update handler, and layout mode controls.
 * @returns Calendar section rendered for popup or drawer mode.
 */
const CalendarSection = ({
  onCalChanged,
  calValue,
  fullScreen,
  drawer,
  vertical,
  quickPriceCalculator,
  availabilityChecker,
  setDrawerShow,
  setPopupShow
}: CalendarSectionProps) => {

  const ref = useRef<any>()

  const getDisabledRanges = (date: any) => {
    // Past dates are blocked to avoid requests for no-longer-bookable periods.
    if (isBefore(new Date(), date)) {
      return true;
    }
    return false;
  };

  const [today, setToday] = useState(calValue[0]);

  // Helper function to get locale from i18n
  const getLocaleFromI18n = () => i18n.language === 'en' ? 'en-US' : 'de-DE';

  const [currentLocale, setCurrentLocale] = useState(getLocaleFromI18n());

  useEffect(() => {
    // Update locale on mount to ensure it's correct
    setCurrentLocale(getLocaleFromI18n());

    const handleLanguageChange = () => {
      setCurrentLocale(getLocaleFromI18n());
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  return (
    <CapsuleTabContentBody>
      <CalendarContainer vertical={vertical} fullScreen={fullScreen} drawer={drawer}>
        <Calendar
          inputRef={ref}
          locale={currentLocale}
          onChange={onCalChanged}
          showWeekNumbers
          value={calValue}
          selectRange={true}
          showDoubleView={true}
          showFixedNumberOfWeeks={false}
          showNeighboringMonth={false}
          minDetail="year"
          tileDisabled={({ date }) => getDisabledRanges(date)}
          // Pinning activeStartDate prevents react-calendar from jumping unexpectedly after selection updates.
          activeStartDate={today} // pass in state var in activeStartDate
          onActiveStartDateChange={data => {
            const { value, activeStartDate, action } = data;
            if (action === 'next' || action === 'next2') {
              setToday(new Date(activeStartDate as Date));
            }
            if (action === 'prev' || action === 'prev2') {
              setToday(new Date(activeStartDate as Date));
            }
            if (action === 'drillDown') {
              setToday(new Date(activeStartDate as Date));
            }
          }}
        />
      </CalendarContainer>
    </CapsuleTabContentBody>
  );
};

export default CalendarSection;
